// Member-facing moderation mutations. The contest-submit form posts here
// after the viewer fills in their reason for contesting an auto-hide; the
// report dialog posts here when a member flags a piece of content.

import { useMutation } from '@tanstack/react-query';
import type { operations } from '@leanin/api-client';

import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';

export type SubmitContestationInput = NonNullable<
  operations['createModerationContestation']['requestBody']
>['content']['application/json'];

export type SubmitContestationResult =
  operations['createModerationContestation']['responses'][201]['content']['application/json'];

export function useSubmitContestation() {
  return useMutation({
    mutationFn: async (
      input: SubmitContestationInput,
    ): Promise<SubmitContestationResult> => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/moderation/contestations',
        { body: input },
      ));
    },
  });
}

export type ReportReason =
  | 'spam'
  | 'harassment'
  | 'self_harm'
  | 'illegal'
  | 'other';

// The content kinds wired to the report dialog today. The endpoint accepts a
// broader target-kind union; extend this when a new kind gets a report UI.
export type ReportTargetKind = 'post' | 'circle' | 'message' | 'comment';

export type CreateReportResult =
  operations['createModerationReport']['responses'][201]['content']['application/json'];

export function useCreateContentReport() {
  return useMutation({
    mutationFn: async (input: {
      targetKind: ReportTargetKind;
      targetId: string;
      reason: ReportReason;
      reasonText?: string;
    }): Promise<CreateReportResult> => {
      const client = getApiClient();
      const body: {
        targetKind: ReportTargetKind;
        targetId: string;
        reason: ReportReason;
        reasonText?: string;
      } = {
        targetKind: input.targetKind,
        targetId: input.targetId,
        reason: input.reason,
      };
      if (input.reasonText !== undefined && input.reasonText.trim().length > 0) {
        body.reasonText = input.reasonText.trim();
      }
      return unwrap(await client.POST(
        '/api/v1/moderation/reports',
        { body },
      ));
    },
  });
}
